import { $http } from "@/utils/https";
import type { ActionContext } from "vuex";
import type { RootState } from "@/store";

type LoadMode = "paginate" | "cursor";

export type BeforeAfterCategory = {
  id: number;
  slug: string;
  name: string;
  image: string;
};

export type BeforeAfterCaseListItem = {
  id: number;
  slug: string;
  imageDesktop: string | null;
  imageMobile: string | null;
};

export type BeforeAfterCaseDetail = {
  id: number;
  slug: string;
  description: string;
  imageDetailed: string;
  category: BeforeAfterCategory;
  products: any;
};

export type PaginationLinks = {
  first: string | null;
  last: string | null;
  prev: string | null;
  next: string | null;
};

export type PaginationMeta = {
  current_page: number;
  from: number | null;
  last_page: number;
  path: string;
  per_page: number;
  to: number | null;
  total: number;
  links?: Array<{ url: string | null; label: string; active: boolean }>;
};

type CasesResponse = {
  data: BeforeAfterCaseListItem[];
  links?: PaginationLinks;
  meta?: PaginationMeta;
};

interface State {
  categories: BeforeAfterCategory[] | null;

  cases: BeforeAfterCaseListItem[] | null;
  casesLinks: PaginationLinks | null;
  casesMeta: PaginationMeta | null;

  caseDetail: BeforeAfterCaseDetail | null;
}

const state: State = {
  categories: null,

  cases: null,
  casesLinks: null,
  casesMeta: null,

  caseDetail: null,
};

const getters = {
  categories: (state: State) => state.categories,

  cases: (state: State) => state.cases,
  casesLinks: (state: State) => state.casesLinks,
  casesMeta: (state: State) => state.casesMeta,

  caseDetail: (state: State) => state.caseDetail,
};

const actions = {
  FETCH_BEFORE_AFTER_CATEGORIES: async ({ commit }: ActionContext<State, RootState>) => {
    try {
      const response = await $http.get<{ data: BeforeAfterCategory[] }>("v1/before-after-categories");
      commit("SET_BEFORE_AFTER_CATEGORIES", response.data.data);
      return response.data.data;
    } catch (e) {
      throw e;
    }
  },

  FETCH_BEFORE_AFTER_CASES: async (
    { commit }: ActionContext<State, RootState>,
    payload: {
      categorySlug: string;
      perPage?: number;
      load?: LoadMode;
      page?: number;
      cursor?: string;
    }
  ) => {
    try {
      const { categorySlug, perPage = 10, load = "paginate", page = 1, cursor } = payload;

      const params: Record<string, any> = { perPage, load };
      if (load === "paginate") params.page = page;
      if (load === "cursor" && cursor) params.cursor = cursor;

      const response = await $http.get<CasesResponse>(`v1/before-after-categories/${categorySlug}/cases`, { params });

      commit("SET_BEFORE_AFTER_CASES", response.data.data || []);
      commit("SET_BEFORE_AFTER_CASES_LINKS", response.data.links || null);
      commit("SET_BEFORE_AFTER_CASES_META", response.data.meta || null);

      return response.data;
    } catch (e) {
      throw e;
    }
  },

  FETCH_BEFORE_AFTER_CASE_DETAIL: async (
    { commit }: ActionContext<State, RootState>,
    payload: {
      categorySlug: string;
      caseSlug: string;
      productsPerPage?: number;
      load?: LoadMode;
      page?: number;
      cursor?: string;
    }
  ) => {
    try {
      const { categorySlug, caseSlug, productsPerPage = 10, load = "paginate", page = 1, cursor } = payload;

      const params: Record<string, any> = { productsPerPage, load };
      if (load === "paginate") params.page = page;
      if (load === "cursor" && cursor) params.cursor = cursor;

      const response = await $http.get<any>(`v1/before-after-categories/${categorySlug}/cases/${caseSlug}`, { params });

      const detail: BeforeAfterCaseDetail = response.data?.data ? response.data.data : response.data;

      commit("SET_BEFORE_AFTER_CASE_DETAIL", detail);
      return detail;
    } catch (e) {
      throw e;
    }
  },
};

const mutations = {
  SET_BEFORE_AFTER_CATEGORIES(state: State, payload: BeforeAfterCategory[]) {
    state.categories = payload;
  },

  SET_BEFORE_AFTER_CASES(state: State, payload: BeforeAfterCaseListItem[]) {
    state.cases = payload;
  },
  SET_BEFORE_AFTER_CASES_LINKS(state: State, payload: PaginationLinks | null) {
    state.casesLinks = payload;
  },
  SET_BEFORE_AFTER_CASES_META(state: State, payload: PaginationMeta | null) {
    state.casesMeta = payload;
  },

  SET_BEFORE_AFTER_CASE_DETAIL(state: State, payload: BeforeAfterCaseDetail) {
    state.caseDetail = payload;
  },

  RESET_BEFORE_AFTER_CASES(state: State) {
    state.cases = null;
    state.casesLinks = null;
    state.casesMeta = null;
  },
  RESET_BEFORE_AFTER_CASE_DETAIL(state: State) {
    state.caseDetail = null;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
